import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './App.css';

//import Components
import DraggableSVG from './DraggableSVG/DraggableSVG';
import ObjectRepresentation from './ObjectRepresentation/ObjectRepresentation'
import LinkableMember from './Members/LinkableMember';
import ArrowFromLine from './Arrows/ArrowFromLine';
import ReflexiveArrowFromLine from './Arrows/ReflexiveArrowFromLine';
import DropDown from './DropDown/DropDown';

//import utility fxns.
import { cornerAwareLine } from './utilities/rect-boundaries';
import { spinalCase, reverseSpinalCase } from './utilities/spinalCase.js'
import CenteredText from './Members/CenteredText';
import createObjectHierarchy, {HIERARCHY_LABELS} from './Hierarchies';
import { set2DElement } from './utilities/immutableTwoDArrayFunctions';

/** TODOS:
 * TODONE
 * 
 + implement draggability onto ObjectReps for just the prototype
 + implement draggability for all members (copy/paste div attributes)
 + implement some horizontal spacing based on place in the array.
 + Try to DRY out member code.
 + implement selective draggability depending on the semantics of the connection (e.g: can only drop a constructor arrow onto Constructor Object).
 + Refactor entire app to use SVG
 + Implement a complexity switch. (to toggle off "constructor" and "prototype" members in Constructors until we introduce Function() as the constructor for Constructors)
 + Implement some draggability of Objects to straighten out arrow by procedure or by user.
 + Implement some way of figuring out which orientation is the one normal to the target: Abandoned - looks terrible without completely complicating the arrow path to end normal to the ending compass rose point. Worked around this by tweaking pixels of arrowheads and creating a "cornerAware" line generator that pushes in the ends of the line generated by rect-boundaries/closestPointsOfTwoRects
 + Improve UI by making the ends of the arrows overlap their endpoints.
 + Implement another kind of Arrow (path as arc?) for reflexive links. (may not be necessary with padding: see once we edit drag handling with function prototypes on. NARRATOR: But it was necessary)
 + Create a way to change to a different group of classes (Note - for code organization purposes, import the array of hierarchies from another file)
 + Controlled drop-down menu of possible hierarchies (animals, furniture, vehicles, people)
 + Created a 1-d array of Arrows of size HIERARCHY_LABELS.length in which to store the arrow Maps for each hierarchy. Had to add another piece of state to check whether the selected hierarchy had just changed, since the rendering arrows depends on the positions of the ObjectRepresentations, which aren't in the DOM until after the first render after the hierarchy changes.
 + Improve performance by creating PureComponents out of SFCs.
 + create a way of noting whether the whole graph is completely linked up, with a way of turning off/on celebrations after the first time - once per (hierarchy) x (functionsDisplayed T/F).
 + Store the "gave the congrats message" state as booleans in each element of a 2-d array: [hierarchies][functionsWereDisplayed], so that it can be displayed up to 2*hierarchies times. 
 + Made a layout function that interprets the object hierarchies and arranges them accordingly.
 + Implemented toggle to move Function stuff to the left vs top (only visible if Function() is visible).
 + in DraggableSVG, if after mouseMove, overlap still exists, makeTransparent.
 *
 * IN PROCESS
 * 
 + TODO: have button + function resets the transforms on the ORs.
 ~ TODO: implement split-screening with classical inheritance. iframe experiment (in parent folder) worked. (use the same hierarchy for both panes, and have the data flow down). Idea: Use linkableMembers to create "extends" links.
 - TODO: keep the control panel buttons/dropdown in a floating/absolutely positioned place, and modify the scroll characteristics? maybe use a hamburger menu, or another iframe/outside both iframes? Also, include checkbox to allow comparison to classical or not.
 - TODO: revisit resize handling (still scrollbars?) - may not be necessary if iframes are used. check now anyway (+: works if controls are not part of calculation, so best inside an iframe?).
 ~ refactor edge detection in DraggableSVG - debug some random overlaps.
 *
 * TODO: 
 *
 - TODO: Give option to save SVG (may also need to grab the OR.css && Arrows.css files, append them and the current value of div? background.)  Link has 3 different solutions. It is possible the first one has been broken for at least a year. https://stackoverflow.com/questions/23218174/how-do-i-save-export-an-svg-file-after-creating-an-svg-with-d3-js-ie-safari-an
 *
 * 
 * MAYBE
 * 
 ? Maybe: create an Object-based version of rect-boundaries (implement exported methods as static, optimize repeated calls by "memoizing" some getBoundingClientRect calls. Clear the cache after creating a "line" object in the higher-order (possibly non-static?) line functions.)
 */

/* Prototypal Container Component
 * =======================
 * TODO: rename to PrototypalInheritance or something, making a parallel case for classical, or pass a boolean parameter that changes the display and behavior from prototypal to classical inheritance.
 * 
 * Note: to avoid id-namespace collisions, only one instance of App should be mounted inside a given Window instance, be that a real Window or an iframe.
 * 
 * Implementation details:
 * Since rendering the arrows require the ObjectRepresentations to have been rendered on-screen in their correct positions previously, we have to use timeouts in those instances when the start/end objects won't have been on-screen continuously. We can't just use the callback-version of setState() either, because that updates state in response to previous values of state, and here we are updating rendering based on previous rendering.
 */
class Prototypal extends Component {
	constructor(props){
		super(props);
		/**
		 * State object
		 * @prop {boolean} functionConstructorDisplayed - switch that toggles on prototype-chain relationships for the constructor functions themselves.
		 * @prop {Object} arrows - a Map Object whose keys are the ids of the starting LinkableMember, and whose corresponding values are the ending ObjectRepresentations' ids.
		 * @prop {Object} dragged - an Object that must contain an "id" property, whose value can be null (this object will be passed down component tree, where the value of its id property will be tested to know whether a LinkableMember is being dragged). Properties:
		 * 		@prop {(null|string)} id - id of LinkableMember's enclosing <g> (group).
		 * 		@prop {function} copy - returns a clone of the LinkableMember instance being dragged (a separate object with the relevant props set to the same value and CSS-determining props set to creating a "transparent ghost").
		 * 		@prop {object} mouseViewBoxCoord - the current position of the mouse (in the DraggableSVG's viewbox coordinates) during a drag.
		 * 		@prop {object} offset - the offset (in viewbox coordinates) of the mouse relative to the (left, top) of the dragged "ghost" copy.
		 * @prop {Object} objects - an Object whose members are the "pseudoclasses" and instances to display.
		 * 		@prop {boolean} isConstructor - states whether each member of state.objects describes either one of:
		 *			true: a ConstructorFunction()
		 *			false: a non-prototype instance (i.e.: a leaf of the prototype tree)
		 * 		@prop {object} [prototype] - If a member is a Constructor, it has a second property called "prototype" that is an object representing the prototypal object for that constructor, whose properties are:
		 * 			@prop {string[]} members - names of members located on the prototypal object
		 * 			@prop {(string|null)} constructor - key of the member in state.objects that represents the constructor that created that prototypal object. If null, that indicates that this Constructor's protoype is the root of the prototype chain (i.e., Object.prototype).
		 * 		@prop {string} constructor If a member is an instance, is has a second property called "constructor", representing everything a non-null value does for someConstructor.prototype.
		 * 		Notes: __proto__ for an instance or prototypal object === .constructor.prototype, which means it would be redundant to store it in state (plus, this is theoretically how we should access the prototypal object without a call to the static Object.getPrototypeOf(myInstance));
		 */
//TODO: comment the new version of state, including how justSwitchedHierarchy serves as a re-render trigger to get the arrows to be drawn after the ORs are.
		const INITIAL_FUNCTION_DISPLAYED_STATE = false;
		const FUNCTION_INITIALLY_TO_LEFT = true;
		const INITIAL_HIERARCHY_INDEX = 1 % HIERARCHY_LABELS.length;
		const objects = createObjectHierarchy(INITIAL_FUNCTION_DISPLAYED_STATE, INITIAL_HIERARCHY_INDEX);
		this.state = {
			functionConstructorDisplayed: INITIAL_FUNCTION_DISPLAYED_STATE,
			svg: null,
			arrows: new Array(HIERARCHY_LABELS.length).fill(null).map(x => new Map()),
			dragged: { id: null },
			hierarchyIndex: INITIAL_HIERARCHY_INDEX,
			justSwitchedHierarchy: false,
			objects,
			fullyLinked: new Array(HIERARCHY_LABELS.length).fill(null).map(x => [false, false]),
			congratsAlreadyDisplayed: new Array(HIERARCHY_LABELS.length).fill(null).map(x => [false, false]),
			functionToLeft: FUNCTION_INITIALLY_TO_LEFT,
			layout: this.calculateLayout(objects, INITIAL_FUNCTION_DISPLAYED_STATE, FUNCTION_INITIALLY_TO_LEFT),
		};
		
	}

	componentDidMount(){
		// While not the "React way", the DOM accessing below is a one-time hack that is allowable because this is a top-level App component that should be the only such instance per window. It works because there is only one guaranteed pseudoclass, Object, and thus only two guaranteed ObjectRepresentations.
		this.setState({
			svg: document.getElementById("Object-constructor").closest("svg")
		})
	}

	calculateLayout = (objects, functionConstructorDisplayed, functionToLeft) => {
		//Build the tree object of node properties whose keys are the same as in the objects parameter. In this first pass over objects, only references to the parent's key are stored.
		const layoutBlockTree = {};
		let rootClassName;
		for (let pseudoclassName in objects){//for every pseudoclass or leaf instance in the object hierarchy..
			const pseudoclass = objects[pseudoclassName];
			layoutBlockTree[pseudoclassName] = pseudoclass.isConstructor
				? {
					parent: pseudoclass.prototype.constructor, //null if tree root, i.e. the object prototype {}
					children: [],
					members: pseudoclass.prototype.members ? pseudoclass.prototype.members.slice() : null
				}
				: {
					parent: pseudoclass.constructor
				};
			if (layoutBlockTree[pseudoclassName].parent === null) rootClassName = pseudoclassName;
		}
		//Then fill in .children[] for every parent stored in the tree. (Couldn't guarantee those arrays exist yet during the 1st pass - safer with 2nd pass)
		for (let pseudoclass in layoutBlockTree){
			if (layoutBlockTree[pseudoclass].parent){
				layoutBlockTree[layoutBlockTree[pseudoclass].parent].children.push(pseudoclass);
			}
		}
		/* Now that we have a tree of inheritance relationships, we have to use it to create a layout.
		 * 1st step: figure out "block" size, where block includes Constructor() and Prototype{} ObjectRepresentations for an actual pseudoclass, or just the leaf itself for an instance.
		 * 2nd step: Calculate depth of every block. 0 = Function "row", 1 = root/Object(), and so on.
		 *		- 1st & 2nd steps are combined into one downward-only pass through the tree.
		 * 3rd step: Calculate layer height for each depth based on max block height
		 * 4th step: Calculate minimum padded block width using iterative tree-traversal with a stack: Max( Σ(min block widths of children), blockWidth )
		 * 5th step: Assign values for {constructorX/Y, prototypeX/Y} based on prior calculations.
				- use INTER_BLOCK_PADDING and INTRA_BLOCK_SEPARATION from default props.
				- for pseudoclasses whose index > parent.children.length/2, put constructor on the right to show the prototype tree more centrally.
		*/

		// 1+2+(part of 5): Traverse the tree and calculate depth, block size, and whether to put children's prototypes on the right
		let stack = [rootClassName];
		let maxDepth = 0;
		while (stack.length){
			//Deal with traversal, get currentNode reference to object in inheritanceLayoutTree
			const currentPseudoClass = stack.pop();
			const currentNode = layoutBlockTree[currentPseudoClass];
			if (currentNode.children){
				stack.push(...currentNode.children);

				//decide which pseudoclass children should show their prototype on the right.
				const nextLayerPseudoClassChildren = currentNode.children.filter(kid => kid !== "Function" && objects[kid].isConstructor);
				for (let kidIndex = Math.ceil(nextLayerPseudoClassChildren.length/2); kidIndex < nextLayerPseudoClassChildren.length; kidIndex++){
					layoutBlockTree[nextLayerPseudoClassChildren[kidIndex]].showConstructorOnRight = true;
				}
			}

			// Calculate depth
			currentNode.depth = currentPseudoClass === "Function"
				? 0 // Function() and function () comprise their own layer that may or may not be there, so we make it a special case.
				: currentNode.parent
					? layoutBlockTree[currentNode.parent].depth + 1
					: 1;//the Object/root pseudoclass.
			maxDepth = Math.max( maxDepth, currentNode.depth);

			// Calculate block height.
			let props = {
				displayFunctionConstructor: functionConstructorDisplayed,
				children: currentPseudoClass
			};
			if (currentNode.children){//block is a pseudoclass
				Object.assign(props, {
					members: currentNode.members,
					isPrototypeInTree: true,
					isConstructor: false
				});
				currentNode.height = ObjectRepresentation.getHeight(props);//prototype's height
				currentNode.prototypeWidth = ObjectRepresentation.getWidth(props);

				Object.assign(props, {//set props to the values necessary for a basic Constructor().
					members: null,
					isConstructor: true,
					isPrototypeInTree: false
				});
				currentNode.constructorWidth = ObjectRepresentation.getWidth(props);

				if (functionConstructorDisplayed){//constructor height guaranteed to be shorter than prototype's unless Function hierarchy shown.
					Object.assign(props, {
						isPrototypeInTree: true,
					});
					currentNode.height = Math.max(
						currentNode.height, ObjectRepresentation.getHeight(props)// function OR's height if Function() is displayed.
					);
					currentNode.constructorWidth = Math.max(currentNode.constructorWidth, ObjectRepresentation.getWidth(props));
				}
				
				currentNode.width = currentNode.prototypeWidth + currentNode.constructorWidth + this.props.INTRA_BLOCK_SEPARATION;
			} else {//block is an instance.
				Object.assign(props, {
					members: null,
					isPrototypeInTree: false,
					isConstructor: false
				});
				currentNode.height = ObjectRepresentation.getHeight(props);
				currentNode.width = ObjectRepresentation.getWidth(props);
			}
		}

		//3: get layer heights.
		const layerHeights = new Array(maxDepth+1);
		const layers = new Array(maxDepth+1);
		const blockNames = Object.keys(layoutBlockTree);
		for (let layerDepth = 0; layerDepth <= maxDepth; layerDepth++){
			const layer = blockNames.filter(blockName => layoutBlockTree[blockName].depth === layerDepth);
			layers[layerDepth] = layer;
			const layerHeight = layer.reduce(
				(maxBlockHeightInLayer, blockName) => Math.max(maxBlockHeightInLayer, layoutBlockTree[blockName].height)
				, 0
			);
			layerHeights[layerDepth] = layerHeight;
		}

		//4: get minimum padded block width:
		stack = [rootClassName];
		while (stack.length){
			const top = stack[stack.length-1];
			const topNode = layoutBlockTree[top];
			if (topNode.children && top !== "Function"){//not a leaf (including Function, which for layout purposes is a special leaf)
				if (topNode.sumOfChildWidths){//then we have begun to send information back up the tree from the leaves, i.e.: this node has already been expanded, it's kids have been processed, so there is therefore a nonzero sum stored. (for Function, with 0 kids, -props.padding(e.g.: -8))
					topNode.originalWidth = topNode.width;// stored for centering purposes.
					topNode.width = Math.max(topNode.width, topNode.sumOfChildWidths);
					if (topNode.parent) layoutBlockTree[topNode.parent].sumOfChildWidths += topNode.width;
					stack.pop();
				} else {//we still have to traverse this branch of the tree down to the leaves -> expand this node.
					topNode.sumOfChildWidths = this.props.INTER_BLOCK_PADDING * (topNode.children.length - 1 -(topNode.children.includes("Function") ? 1 : 0));
					stack.push(...topNode.children);
				}
			} else {//is a leaf.
				if (top !== "Function") layoutBlockTree[topNode.parent].sumOfChildWidths += topNode.width; //since Function is presented in the 0th layer, its block doesn't occupy space in the same layer as its tree-siblings and tree-cousins.
				stack.pop();
			}
		}
		//The following line makes the layout function act as if both the Function and Object blocks are in the first layer. Later, we'll reposition the Function block and shift the remainder of the diagram to the right, resulting in the Function pseudoclass being positioned to the left side of the diagram.
		if (functionConstructorDisplayed && functionToLeft) layerHeights[0] = 0;

		//5: create layout from calculated data
		const layoutState = {};
		let yOffset = this.props.INTER_BLOCK_PADDING;
		for (let layerIndex = 0; layerIndex < layers.length; layerIndex++){
			let xOffset = this.props.INTER_BLOCK_PADDING;
			let previousParent = "";
			for (let blockIndex = 0; blockIndex < layers[layerIndex].length; blockIndex++){
				//manually reset the xOffset if this block and the previous one's parents are different.
				const pseudoclass = layers[layerIndex][blockIndex];
				const blockNode = layoutBlockTree[pseudoclass];
				let parentNode = blockNode.parent ? layoutBlockTree[blockNode.parent] : null;
				if (blockNode.parent && blockNode.parent !== previousParent) {
					previousParent = blockNode.parent;
					//check for undefined in case of Function layer [0] (Object.xOffset will be defined when processing layer[1])
					if (parentNode.xOffset !== undefined) xOffset = parentNode.xOffset;
				}//otherwise, just keep on incrementing it by the width of the just-layed-out block (at end of for loop)
				blockNode.xOffset = xOffset;

				//lay out the objects within the block.
				const blockHorizontalPadding = blockNode.originalWidth && pseudoclass !== "Function" // testing for pseudoclass vs. leaf, with exceptions for Function.
					? Math.floor(blockNode.width - blockNode.originalWidth)/2 //pseudoclass
					: (parentNode.width - (pseudoclass === "Function" ? blockNode.width : parentNode.sumOfChildWidths))/2; //leaf (width >= ΣchildWidths, so Δ >= 0)
				const firstObjectInBlock = {
					x: xOffset + blockHorizontalPadding,
					y: yOffset
				};
				if (blockNode.children){//pseudoclass
					const constructorPosition = blockNode.showConstructorOnRight
						? { //first Object's xOffset + first object's width + CONSTANT
							x: firstObjectInBlock.x + blockNode.prototypeWidth + this.props.INTRA_BLOCK_SEPARATION,
							y: yOffset
						  }
						: firstObjectInBlock;
					const prototypePosition = blockNode.showConstructorOnRight
						? firstObjectInBlock
						: { //first Object's xOffset + first object's width + CONSTANT
							x: firstObjectInBlock.x + blockNode.constructorWidth + this.props.INTRA_BLOCK_SEPARATION,
							y: yOffset
						  };
					layoutState[pseudoclass] = { constructorPosition, prototypePosition };
				} else {//instance
					layoutState[pseudoclass] = firstObjectInBlock;
				}

				//prepare for the next block in the layer.
				xOffset += blockNode.width + this.props.INTER_BLOCK_PADDING;
			}
			//prepare for the next layer.
			yOffset += layerHeights[layerIndex] + (layerHeights[layerIndex] ? this.props.INTER_BLOCK_PADDING : 0); // padding was only used if the layer wasn't empty.
		}
		if (functionConstructorDisplayed && functionToLeft){
			//manually set a layout which allows the arrows leading to and within the Function pseudoclass to be perpendicular to those representing the prototype tree.
			const functionBlock = layoutBlockTree.Function;//convenience variable for readability.

			//set the positions of Function() and function ().
			layoutState.Function = {
				constructorPosition: {
					x: 3 * this.props.INTER_BLOCK_PADDING,
					y: layoutState.Function.constructorPosition.y
				},
				prototypePosition: {
					x: this.props.INTER_BLOCK_PADDING,
					y: layoutState.Function.constructorPosition.y + functionBlock.height + 4 * this.props.INTER_BLOCK_PADDING,
				}
			}
			//adjust the positions of the remaining pseudoclasses.
			const xAdjustment = functionBlock.constructorWidth + 8 * this.props.INTER_BLOCK_PADDING;//altered function block's width + inter-block-padding.
			for (let pseudoclass in layoutState){
				if (pseudoclass !== "Function"){
					const blockNode = layoutState[pseudoclass];
					if (blockNode.constructorPosition){
						blockNode.constructorPosition.x += xAdjustment;
						blockNode.prototypePosition.x += xAdjustment;
					} else {
						blockNode.x += xAdjustment;
					}			
				}
			}
		}

		return layoutState;
	};
	
	toggleFunctionLayoutToLeft = () => {
		this.setState({
			functionToLeft: !this.state.functionToLeft,
			layout: this.calculateLayout(this.state.objects, this.state.functionConstructorDisplayed, !this.state.functionToLeft)
		})
		setTimeout(this.updateArrowPositions, 0);
	};

	/**
	 * @method selectHierarchy
	 * @description - event handler for hierarchy <DropDown>
	 * @param {object} evt - selection event to handle
	 */
	selectHierarchy = (evt) => {
		const objects = createObjectHierarchy(this.state.functionConstructorDisplayed, +evt.target.value);
		this.setState({
			hierarchyIndex: +evt.target.value,
			objects,
			arrows: this.state.arrows,
			justSwitchedHierarchy: +evt.target.value !== this.state.hierarchyIndex,
			fullyLinked: this.getCurrentLinkageState(),
			layout: this.calculateLayout(objects, this.state.functionConstructorDisplayed, this.state.functionToLeft)
		})
	};

	/**
	 * @method toggleFunctionHierarchy
	 * @description handler that toggles whether the Function() and function () objects are displayed.
	 */
	toggleFunctionHierarchy = () => {
		const objects = createObjectHierarchy(!this.state.functionConstructorDisplayed, this.state.hierarchyIndex);
		this.setState({
			functionConstructorDisplayed: !this.state.functionConstructorDisplayed,
			objects,
			fullyLinked: this.getCurrentLinkageState(),
			layout: this.calculateLayout(objects, !this.state.functionConstructorDisplayed, this.state.functionToLeft)
		})
		setTimeout(this.updateArrowPositions, 0);
	}
	
	/**
	 * @method updateArrowPositions
	 * @description updates the starting and ending locations of all arrows when ObjectRepresentations get relocated by dragging or translation.
	 */
	updateArrowPositions = () => {
		this.setState({arrows: this.state.arrows});
	}

	/** LAST ORGANIZED POINT: PICK UP CODE CLEANUP ABOVE */
	/**
	 * @method dragStartHandler
	 * @description responds to a mousedown on a LinkableMember, updating the state.dragged object with all needed information.
	 */
	dragStartHandler = evt => {
		evt.stopPropagation();
		const rect = evt.target;
		const selectedMember = rect.parentNode;
		
		//use the selected <g>'s <text> and id to create a function that returns a new LinkableMember that is semitransparent and doesn't respond to mouse events.
		const textObject = selectedMember.lastChild;
		const text = textObject.textContent;

		const id = selectedMember.id;
		const parentGroupId = selectedMember.id.replace("-"+text, "");

		const copyOfMemberBeingDragged = (left, top) =>
			<LinkableMember
				parentObjectName={parentGroupId}
				x={left} y={top}
				width={rect.width.baseVal.value}
				height={rect.height.baseVal.value}
				fontSize={+textObject.getAttribute("font-size")}//the leading (+) converts the attribute string to a number the fastest per jsperf
				rectClass="dragged-member-rect-copy"
			>
				{text}
			</LinkableMember>;
		
		// get the mouse's position and offset relative to (left, top) of the selected group.
		const mouseViewBoxCoord = this.getMousePosition(evt);
		const {e: x, f: y} = selectedMember.getCTM();
		const offset = {
			x: mouseViewBoxCoord.x - x,
			y: mouseViewBoxCoord.y - y
		};

		//update state with the copy-generator fxn, and the relevant data about the LinkableMember being dragged.
		this.setState({
			dragged:{
				id,
				copy: copyOfMemberBeingDragged,	
				mouseViewBoxCoord,
				offset,
			}
		});
	};

	/**
	 * @method getMousePosition
	 * @description returns the viewbox coordinates of the mouse pointer or the first touch of a multitouch input event
	 * @param  {Object} evt - the event object passed by the DOM
	 */
	getMousePosition(evt){
		if (evt.touches) evt = evt.touches[0];
		return this.viewboxCoordinates(evt.clientX, evt.clientY);
	}

	/**
	 * @method viewboxCoordinates
	 * @description (X,Y) in client coordinates will return correct SVG viewbox coordinates
	 * @param  {Number} clientX
	 * @param  {Number} clientY
	 * @returns {Object} - {x, y} in SVG viewbox coordinates
	 */
	viewboxCoordinates(clientX, clientY){
		const CTM = this.state.svg.getScreenCTM();
		return {
			//Translation of below:
			//(DOM.x - viewbox.Offset)/scaling factor of screen/viewbox rect size.
			x: (clientX - CTM.e) / CTM.a,
			y: (clientY - CTM.f) / CTM.d
		};
	}
	
	/**
	 * @method dragHandler
	 * @description responds to a mouseMove on the DraggableSVG if a LinkableMember is currently being dragged by updating state.dragged.mouseViewBoxCoord
	 */
	dragHandler = evt => {
		if (this.state.dragged.id){
			evt.preventDefault();
			evt.stopPropagation();

			const newDragState = Object.assign({}, this.state.dragged);
			newDragState.mouseViewBoxCoord = this.getMousePosition(evt);

			this.setState({dragged: newDragState});
		}
	};
	
	/**
	 * @method dragEndHandler
	 * @description responds to a mouseUp or mouseLeave on the DraggableSVG if a LinkableMember is currently being dragged by updating state.arrows with the results of the drag and by resetting the state.dragged object to its initial state.
	 */
	dragEndHandler = evt => {
		if (this.state.dragged.id){
			let userLinkedCorrectly = false;
			let droppedMemberType;

			const targetObject = evt.target.closest(".object");
			if (targetObject){//then we know the LinkableMember was dropped onto an ObjectRepresentation.

				//extract the inheritance relationships for the droppedMember from state.dragged.id
				const droppedMemberInfo = this.state.dragged.id.match(/^(.+?)(-prototype|-constructor)?-(\w+)$/);
				droppedMemberType = droppedMemberInfo[3]; //inheritance member type
				const objectTypeContainingDroppedMember = droppedMemberInfo[2] && droppedMemberInfo[2].slice(1);//can move this into __proto__ case. //if the member didn't belong to an instance, evaluates to (prototype|constructor).
				const pseudoClass = reverseSpinalCase(droppedMemberInfo[1]);//will be the relevant pseudoClass or instance name. While not the most completely specific name, it greatly facilitates the readability to keep it short and relevant to non-instances.
				
				//determine what the correct target should be based on which member was dropped.
				let correctTargetId;
				switch(droppedMemberType){
					case ("prototype"):
						correctTargetId = pseudoClass + "-prototype";
						break;
					case ("constructor"):
						correctTargetId = objectTypeContainingDroppedMember === "constructor"
							? "Function-constructor"
							: pseudoClass + "-constructor";
						break;
					case ("__proto__"):
						// How we respond depends on what type of ObjectRepresentation contained the dragged-n-dropped LinkableMember...
						if (objectTypeContainingDroppedMember === "constructor"){
							//all constructors are functions, and thus cloned from archetypal function.
							correctTargetId = "Function-prototype";
						} else if (objectTypeContainingDroppedMember === "prototype"){
							//we can look in this.state.objects to find which Constructor was used to create this prototype object
							correctTargetId = this.state.objects[pseudoClass].prototype.constructor + "-prototype"
						} else {//the object whose __proto__ property it is was an instance-leaf on the prototype tree, i.e. cloned off it's constructor's "prototype" member.
							correctTargetId = this.state.objects[pseudoClass].constructor + "-prototype"
						}
						break;
					default:
						break;
				}

				userLinkedCorrectly = correctTargetId === targetObject.id;
			}
			if (userLinkedCorrectly){
				//Note: Map.prototype.set() returns the Map object, so we have to reassign state.arrows to itself (the array of maps);
				this.state.arrows[this.state.hierarchyIndex].set(this.state.dragged.id, {
					id: targetObject.id,
					inProtoTree: droppedMemberType === "__proto__"
				})

				this.setState({
					arrows: this.state.arrows,
					dragged: { id: null },
				});
			} else {
				//Drop failed: try deleting the arrow which starts at the dragged element. If such an arrow exists, the delete() call will return true, therefore we should update state with the altered Map. 
				const nonDraggingState = { dragged: { id: null } };
				if (this.state.arrows[this.state.hierarchyIndex].delete(this.state.dragged.id)) nonDraggingState.arrows = this.state.arrows;
				this.setState( nonDraggingState );
			}
			this.setState(prevState => ({
				fullyLinked: this.getCurrentLinkageState()
			}));
		}
	};

	/**
	 * @method svgMappedLine
	 * @description Wrapper function for the line-outputting fxns of rect-boundaries.js that maps client coordinates to SVG viewbox coordinates.
	 * @param {object} line - a line in client coordinates.
	 * @returns {object} the line mapped onto the DraggableSVG's coordinate system.
	 */
	svgMappedLine = line => {
		//conditional guards against null case.
		return line && {
			start: this.viewboxCoordinates(line.start.x - window.pageXOffset, line.start.y - window.pageYOffset),
			end: this.viewboxCoordinates(line.end.x - window.pageXOffset, line.end.y - window.pageYOffset)
		}
	};

	/**
	 * @method getCurrentLinkageState
	 * @description updates the appropriate element in state.fullyLinked with true if every visible LinkableMember is linked, false otherwise.
	 */
	getCurrentLinkageState(){
		let numDisplayedArrows = 0;
		this.state.arrows[this.state.hierarchyIndex].forEach((target, originId) => numDisplayedArrows +=
			this.state.functionConstructorDisplayed
				? 1 //if Functions displayed, every arrow counts.
				: originId.includes("Function") || target.id.includes("Function")
					? 0 //if Fxns !displayed, arrows to/from Function hierarchy don't count...
					: 1 //but every other arrow does.
		);
		/* Every Constructor() creates an arrow starting at:
		 * 		.prototype on the Constructor()
		 * 		.constructor on the prototype object
		 * 		.__proto__ on the prototype object (except the protochain root)
		 * if state.FunctionsDisplayed, then they also produce: (incl. for Function() which is now in this.state.objects)
		 * 		.constructor on the Constructor()
		 * 		.__proto__ on the Constructor()
		 * Every Leaf-Instance produces on from .__proto__
		 * So, (3 or 5 per Constructor) + (1 per Leaf) - 1.
		 */
		let numArrowsNeeded = -1; //for the protochain root
		for (let obj in this.state.objects){
			numArrowsNeeded += this.state.objects[obj].isConstructor
				? this.state.functionConstructorDisplayed ? 5 : 3
				: 1;
		}

		return set2DElement(
			this.state.fullyLinked,
			this.state.hierarchyIndex,
			this.state.functionConstructorDisplayed ? 1 : 0,
			numDisplayedArrows === numArrowsNeeded
		);
	}
	
	render() {
console.log("App render");

		//Create an array of Arrow Components.
		const arrowsJSX = [];
		//The reason for the if/else and toggle as a piece of state is: the arrows can't render unless the ObjectRepresentaions already have been, which isn't the case if we just switched the hierarchy. This code triggers a re-render, and on the second pass, the arrows are filled in.
		if (this.state.justSwitchedHierarchy){
			setTimeout(()=>this.setState({justSwitchedHierarchy: false}), 0);
		} else {
			//The line below works because the set of arrows maps many-to-one (val, key).
			this.state.arrows[this.state.hierarchyIndex].forEach((target, originId) => {
				const line = this.svgMappedLine(cornerAwareLine(3, originId, target.id, "sides corners"));

				//only reason a line should be null is if one end landed on a Function-hierarchy member or object and this.state.functionConstructorDisplayed is false.
				if (line){
					if (originId.includes(target.id)){
						arrowsJSX.push(
							<ReflexiveArrowFromLine
								key={originId}
								id={`${originId}-to-${target.id}`}
								line={line}
								color={target.inProtoTree ? 'darkorange' : 'teal'}
								strokeWidth={4.5}
							/>
						);
					} else {
						arrowsJSX.push(
							<ArrowFromLine
								key={originId}
								id={`${originId}-to-${target.id}`}
								line={line}
								color={target.inProtoTree ? 'darkorange' : 'teal'}
								strokeWidth={4.5}
							/>
						);
					}
				} 
			});
		}
		
		//Create an array of ObjectRepresentations.
		let { objects } = this.state;
		const objectsJSX = [];
		Object.keys(objects).forEach((objKey, idx) => {
			const layout = this.state.layout[objKey];
			objectsJSX.push(
				<ObjectRepresentation
					key={spinalCase(objKey)+idx}
					x={layout.x || layout.constructorPosition.x} y={layout.y || layout.constructorPosition.y}
					isConstructor={objects[objKey].isConstructor}
					displayFunctionConstructor={this.state.functionConstructorDisplayed}
					memberDragStartHandler={this.dragStartHandler}
					dragData = {this.state.dragged.id}
					arrowData = {this.state.arrows[this.state.hierarchyIndex]}
				>
					{objKey}
				</ObjectRepresentation>
			);
			if (objects[objKey].isConstructor){
				objectsJSX.push(
					<ObjectRepresentation
						key={spinalCase(objKey)+"proto"+idx}
						x={layout.prototypePosition.x} y={layout.prototypePosition.y}
						members={objects[objKey].prototype.members}
						isPrototypeInTree
						memberDragStartHandler={this.dragStartHandler}
						dragData = {this.state.dragged.id}
						arrowData = {this.state.arrows[this.state.hierarchyIndex]}
					>
						{objKey}
					</ObjectRepresentation>
				);
			}
		});
		
		//create a congratulatory message for linking up the visible LinkableMembers. Currently only fires once.
		// NOTE: React will throw a "prop type" warning because we're animating the text element rendered by CenteredText by passing in the <animate> tag and a string as children instead of one string. Let it wail. It is the Matrix and I am Neo.
		let pageOffsetMappedToSvg = this.state.svg ? this.viewboxCoordinates(0,0) : {x:0, y:0};
	
		//figure out whether to display that message, and if we should, set a timer to turn it off after 5s.
		const fullyLinked = this.state.fullyLinked[this.state.hierarchyIndex][this.state.functionConstructorDisplayed ? 1 : 0];
		const congratsNotYetDisplayed = !this.state.congratsAlreadyDisplayed[this.state.hierarchyIndex][this.state.functionConstructorDisplayed ? 1 : 0];
		if (congratsNotYetDisplayed && fullyLinked){
			setTimeout(() => this.setState({
				congratsAlreadyDisplayed: set2DElement(
					this.state.congratsAlreadyDisplayed,
					this.state.hierarchyIndex,
					this.state.functionConstructorDisplayed ? 1 : 0,
					true
				)
			}), 5000);
		}
		return <div>
			<button onClick={this.toggleFunctionHierarchy}>Toggle Functions</button>
			{this.state.functionConstructorDisplayed &&
			<button onClick={this.toggleFunctionLayoutToLeft}>
				{this.state.functionToLeft ? "Move Function() To Top" : "Move Function() to Left"}
			</button>}
			<DropDown
				selectedIndex={this.state.hierarchyIndex}
				changeHandler={this.selectHierarchy}
			>
				{HIERARCHY_LABELS}
			</DropDown>
			<DraggableSVG
				classesForDragging = {['object']}
				allowOverlap={false}
				makeTransparentIfCollidingDragResultsInOverlap={true}
				unitsOfOverlapNecessaryForTransparency={2}
				svgChildDragHandler={this.updateArrowPositions}
				memberDragHandler={this.dragHandler}
				memberDragEndHandler={this.dragEndHandler}
				dragData={this.state.dragged}
				classesExcludedFromResizeTranslation={['arrow']}
				padding = {this.props.INTER_BLOCK_PADDING}
			>
				{objectsJSX}
				{this.state.dragged.copy &&
				this.state.dragged.copy(
					this.state.dragged.mouseViewBoxCoord.x - this.state.dragged.offset.x,
					this.state.dragged.mouseViewBoxCoord.y - this.state.dragged.offset.y
				)}
				{arrowsJSX}
				{ fullyLinked && congratsNotYetDisplayed && <CenteredText
					x={pageOffsetMappedToSvg.x} y={pageOffsetMappedToSvg.y}
					width={window.innerWidth} height={window.innerHeight}
					fontSize={60}
					classes="congrats"
				>
					<animate attributeName="fill" from="red" to="green" dur="1s" repeatCount="indefinite"/>
					{"Congratulations!"}
				</CenteredText>}
			</DraggableSVG>
		</div>;
	}
}

Prototypal.propTypes = {
	INTER_BLOCK_PADDING: PropTypes.number,
	INTRA_BLOCK_SEPARATION: PropTypes.number
};

Prototypal.defaultProps = {
	INTER_BLOCK_PADDING: 16,
	INTRA_BLOCK_SEPARATION: 8
};

export default Prototypal;
